/**
 * Shop-Feel Block-Komponenten (Welle 8).
 *
 * - HeroSlider: Auto-rotierender Banner mit konfigurierbaren Slides + CTA
 * - PromoStrip: Horizontal scrollende Discount-Code-Pills
 * - TrendingSection: Auto Top-Seller Strip basierend auf total_sales
 * - DefaultBrandHero: schöner Gradient-Banner wenn keine Cover/Logo/Avatar gesetzt
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles, Tag, TrendingUp } from "lucide-react";
import { type HeroSlide, type PromoPill, type PublicProduct } from "../lib/page";
import { navigate } from "../lib/router";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
  bg: string;
  gradientFrom: string;
  gradientTo: string;
};

const fmtEuro = (s: string | null): string | null => {
  if (!s) return null;
  return `${s} €`;
};

// ============================================================
// Hero-Slider — Auto-Rotation, Dots, Touch-Swipe
// ============================================================

export function HeroSlider({
  slides,
  theme,
  isDark,
}: {
  slides: HeroSlide[];
  theme: ThemeLike;
  isDark: boolean;
}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const t = window.setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, 5500);
    return () => window.clearInterval(t);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const goTo = (next: number) => setIdx(((next % slides.length) + slides.length) % slides.length);

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) goTo(idx + (delta > 0 ? -1 : 1));
        touchStartX.current = null;
      }}
    >
      {/* Slide */}
      <div className="relative h-56 md:h-80 lg:h-96 w-full">
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            aria-hidden={i !== idx}
          >
            {s.image_url ? (
              <img
                src={s.image_url}
                alt=""
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            ) : (
              <div
                className="w-full h-full"
                style={{
                  background: `linear-gradient(135deg, ${
                    s.accent_color || theme.gradientFrom
                  }, ${theme.gradientTo})`,
                }}
              />
            )}
            {/* Dark overlay für Text-Lesbarkeit */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)",
              }}
            />
            {/* Slide-Content */}
            {(s.headline || s.subtitle || s.cta_label) && (
              <div className="absolute inset-0 flex items-end">
                <div className="mx-auto max-w-6xl w-full px-4 md:px-8 pb-6 md:pb-10">
                  <div className="max-w-xl text-white">
                    {s.headline && (
                      <h2 className="text-2xl md:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-lg">
                        {s.headline}
                      </h2>
                    )}
                    {s.subtitle && (
                      <p className="mt-1.5 md:mt-2 text-sm md:text-base opacity-95 drop-shadow-md max-w-md">
                        {s.subtitle}
                      </p>
                    )}
                    {s.cta_label && (
                      <a
                        href={s.cta_url || "#produkte"}
                        onClick={(e) => {
                          if (s.cta_url && s.cta_url.startsWith("/")) {
                            e.preventDefault();
                            navigate(s.cta_url);
                          } else if (!s.cta_url || s.cta_url === "#produkte") {
                            e.preventDefault();
                            const el = document.getElementById("produkte");
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.scrollY - 60;
                              window.scrollTo({ top: y, behavior: "smooth" });
                            }
                          }
                        }}
                        target={s.cta_url && s.cta_url.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="mt-3 md:mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold shadow-lg transition-transform hover:scale-[1.02]"
                        style={{ background: s.accent_color || theme.accent, color: "#fff" }}
                      >
                        {s.cta_label}
                        <ArrowRight className="size-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Arrows + Dots — nur bei 2+ Slides */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => goTo(idx - 1)}
            className="hidden md:grid place-items-center absolute left-3 top-1/2 -translate-y-1/2 size-9 rounded-full backdrop-blur-sm transition hover:scale-105"
            style={{ background: "rgba(255,255,255,0.85)", color: "#0a0a0a" }}
            aria-label="Vorheriger Slide"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            onClick={() => goTo(idx + 1)}
            className="hidden md:grid place-items-center absolute right-3 top-1/2 -translate-y-1/2 size-9 rounded-full backdrop-blur-sm transition hover:scale-105"
            style={{ background: "rgba(255,255,255,0.85)", color: "#0a0a0a" }}
            aria-label="Nächster Slide"
          >
            <ChevronRight className="size-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
      {/* Gradient zum Page-Body — sanfter Übergang */}
      <div
        className="absolute inset-x-0 bottom-0 h-8 pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${theme.bg})`,
        }}
      />
      {/* Anti-isDark warning */}
      {isDark ? null : null}
    </div>
  );
}

// ============================================================
// Promo-Strip — Horizontale Pill-Liste mit Discount-Codes
// ============================================================

export function PromoStrip({
  pills,
  productPromos,
  theme,
  isDark,
}: {
  pills: PromoPill[];
  productPromos: PublicProduct[];
  theme: ThemeLike;
  isDark: boolean;
}) {
  // Merge: explizite promo_strip Pills + Produkte mit discount_code
  const combined: { label: string; code?: string; hint?: string; url?: string }[] = [
    ...pills.map((p) => ({ ...p })),
    ...productPromos.map((p) => ({
      label: p.name,
      code: p.discount_code || undefined,
      hint: p.discount_hint || undefined,
      url: p.wc_product_id ? `/p/${p.wc_product_id}` : p.permalink,
    })),
  ];

  if (combined.length === 0) return null;

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-3"
      aria-label="Aktuelle Angebote"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-3.5" style={{ color: theme.accent }} />
        <span
          className="text-[10px] uppercase tracking-wider font-bold"
          style={{ color: theme.inkMuted }}
        >
          Aktuelle Angebote
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {combined.map((p, i) => (
          <a
            key={`${p.label}-${i}`}
            href={p.url || "#produkte"}
            onClick={(e) => {
              if (p.url && p.url.startsWith("/")) {
                e.preventDefault();
                navigate(p.url);
              } else if (!p.url || p.url === "#produkte") {
                e.preventDefault();
                const el = document.getElementById("produkte");
                if (el) {
                  const y = el.getBoundingClientRect().top + window.scrollY - 60;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }
            }}
            target={p.url && p.url.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="snap-start flex-shrink-0 inline-flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-transform hover:scale-[1.03]"
            style={{
              borderColor: theme.accent,
              background: isDark ? "rgba(255,255,255,.05)" : "#fff",
              color: theme.ink,
            }}
          >
            <Tag className="size-3.5 flex-shrink-0" style={{ color: theme.accent }} />
            <span className="truncate max-w-[180px]">{p.label}</span>
            {p.code && (
              <span
                className="font-mono px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ background: theme.accent + "20", color: theme.accent }}
              >
                {p.code}
              </span>
            )}
            {p.hint && !p.code && (
              <span className="text-[10px] font-normal italic" style={{ color: theme.inkMuted }}>
                {p.hint}
              </span>
            )}
          </a>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// Trending-Section — Top-Seller Strip (basierend auf total_sales)
// ============================================================

export function TrendingSection({
  products,
  agentCode,
  theme,
  isDark,
  isB2bLocked,
  limit = 8,
}: {
  products: PublicProduct[];
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
  isB2bLocked: boolean;
  limit?: number;
}) {
  const top = [...products]
    .filter((p) => (p.total_sales || 0) > 0)
    .sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0))
    .slice(0, limit);

  if (top.length < 3) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 mt-2 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="size-4" style={{ color: theme.accent }} />
        <h2 className="text-base md:text-lg font-bold" style={{ color: theme.ink }}>
          Trending jetzt
        </h2>
        <span className="text-[11px]" style={{ color: theme.inkMuted }}>
          · meistverkauft
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {top.map((p) => (
          <TrendingCard
            key={p.id}
            product={p}
            theme={theme}
            isDark={isDark}
            isB2bLocked={isB2bLocked}
            agentCode={agentCode}
          />
        ))}
      </div>
    </section>
  );
}

function TrendingCard({
  product: p,
  theme,
  isDark,
  isB2bLocked,
  agentCode: _agentCode,
}: {
  product: PublicProduct;
  theme: ThemeLike;
  isDark: boolean;
  isB2bLocked: boolean;
  agentCode: string;
}) {
  const href = !isB2bLocked && p.wc_product_id ? `/p/${p.wc_product_id}` : p.permalink;
  const external = !href.startsWith("/");
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={(e) => {
        if (external) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      }}
      className="snap-start flex-shrink-0 w-36 md:w-44 rounded-xl overflow-hidden border transition hover:-translate-y-0.5 group"
      style={{
        background: theme.cardBg,
        borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
      }}
    >
      <div
        className="aspect-square overflow-hidden relative"
        style={{ background: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)" }}
      >
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : null}
        {(p.total_sales || 0) > 50 && (
          <span
            className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded backdrop-blur-sm"
            style={{ background: "rgba(255,255,255,.92)", color: theme.accent }}
          >
            🔥 Bestseller
          </span>
        )}
      </div>
      <div className="p-2.5">
        <div
          className="text-xs md:text-sm font-medium leading-tight line-clamp-2 min-h-[2.25rem]"
          style={{ color: theme.ink }}
        >
          {p.name}
        </div>
        <div className="mt-1.5">
          {p.price ? (
            <div className="text-sm font-bold" style={{ color: theme.accent }}>
              {fmtEuro(p.price)}
            </div>
          ) : (
            <div className="text-[10px] font-semibold" style={{ color: theme.accent }}>
              Login für Preis
            </div>
          )}
        </div>
      </div>
    </a>
  );
}

// ============================================================
// DefaultBrandHero — schöner Gradient-Banner für leere Shops
// ============================================================

export function DefaultBrandHero({
  brandName,
  initials,
  theme,
  agentCode,
}: {
  brandName: string;
  initials: string;
  theme: ThemeLike;
  agentCode: string;
}) {
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
      }}
    >
      {/* Dekorative geometric shapes */}
      <div
        className="absolute top-0 right-0 size-64 md:size-96 rounded-full opacity-20 -translate-y-1/4 translate-x-1/4"
        style={{ background: "rgba(255,255,255,0.4)" }}
      />
      <div
        className="absolute bottom-0 left-0 size-48 md:size-64 rounded-full opacity-10 translate-y-1/3 -translate-x-1/3"
        style={{ background: "rgba(255,255,255,0.5)" }}
      />

      <div className="relative mx-auto max-w-6xl px-4 py-10 md:py-16">
        <div className="flex items-center gap-4 md:gap-6">
          <div
            className="size-16 md:size-24 rounded-2xl grid place-items-center text-white text-2xl md:text-4xl font-bold flex-shrink-0 shadow-xl"
            style={{
              background: "rgba(0,0,0,0.15)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            {initials || "DP"}
          </div>
          <div className="min-w-0 text-white">
            <div className="text-xs md:text-sm uppercase tracking-wider opacity-90 font-semibold">
              {agentCode}.dpconnect.de
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mt-1 drop-shadow">
              {brandName}
            </h1>
            <p className="mt-2 text-sm md:text-base opacity-95 max-w-xl">
              Dein Fachhandel für Großhandel-Preise · 8.000+ Produkte ·
              Versand 24h
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
